import DialogModal from '@/components/DialogModal';
import InputDuration from '@/components/InputDuration';
import RPMDateTimePickerWithPlaceholder from '@/components/RPMDateTimePicker/RPMDateTimePickerWithPlaceholder';
import StyledModal from '@/components/StyledModal';
import { DAY } from '@/constants/calendar';
import {
  useCronofyCheckCalendarPermissions,
  useCronofyManageExternalEvent,
  useCronofyRequestExtendedPermissions,
} from '@/services/cronofy/hooks';
import { queryClient } from '@/services/graphql/queryClient';
import { IconPencilDraw, IconTrash } from '@/theme/icons';
import { MyEvent } from '@/types/calendar';
import { durationStringToMinutes, getEventLength, minutesToDurationString } from '@/utils/calendar';
import { CronofyExtendedPermissionsManager, hasUnrestrictedPermissions } from '@/utils/cronofyExtendedPermissions';
import { getDateFnsLocale } from '@/utils/dateLocale';
import rem from '@/utils/rem';
import { refetchQueries } from '@/utils/tanStackQuery';
import {
  Button,
  Divider,
  Flex,
  HStack,
  Icon,
  IconButton,
  ModalBody,
  ModalCloseButton,
  ModalFooter,
  ModalHeader,
  Text,
  Tooltip,
  VStack,
  useDisclosure,
  useToast,
} from '@chakra-ui/react';
import { addMinutes, differenceInMinutes, format } from 'date-fns';
import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';

type Props = {
  event: MyEvent;
  isOpen: boolean;
  title: string;
  onClose: () => void;
  onEventDeleted?: () => void;
};

function EventDetailModal({ event, isOpen, title, onClose, onEventDeleted }: Props) {
  const eventLength = useMemo(() => getEventLength(event), [event]);
  const { t, i18n } = useTranslation('EventDetailModal');
  // Get the appropriate locale for date-fns based on current language
  const dateLocale = getDateFnsLocale(i18n.language);
  const toast = useToast();

  // Calculate initial duration from event start and end
  const initialDuration = useMemo(() => {
    if (event.start && event.end) {
      const minutes = differenceInMinutes(new Date(event.end), new Date(event.start));
      return minutesToDurationString(minutes);
    }
    return '01:00'; // Default 1 hour
  }, [event.start, event.end]);

  // State for dialogs
  const [isDeleting, setIsDeleting] = useState(false);
  const [isRequestingPermissions, setIsRequestingPermissions] = useState(false);
  const [isUpdating, setIsUpdating] = useState(false);

  // State for event editing
  const [newStartTime, setNewStartTime] = useState<Date>(new Date(event.start));
  const [duration, setDuration] = useState<string>(initialDuration);
  const [timeValidationError, setTimeValidationError] = useState<string | null>(null);

  // Dialog hooks
  const { isOpen: isConfirmDialogOpen, onOpen: onOpenConfirmDialog, onClose: onCloseConfirmDialog } = useDisclosure();

  const {
    isOpen: isPermissionsDialogOpen,
    onOpen: onOpenPermissionsDialog,
    onClose: onClosePermissionsDialog,
  } = useDisclosure();

  const { isOpen: isEditDialogOpen, onOpen: onOpenEditDialog, onClose: onCloseEditDialog } = useDisclosure();

  const {
    isOpen: isRescheduleConfirmDialogOpen,
    onOpen: onOpenRescheduleConfirmDialog,
    onClose: onCloseRescheduleConfirmDialog,
  } = useDisclosure();

  const {
    isOpen: isStartDatePickerOpen,
    onOpen: onStartDatePickerOpen,
    onClose: onStartDatePickerClose,
  } = useDisclosure();

  // Check if this is an external event
  const isExternalEvent = event.isExternal && event.calendarId && event.id;

  // Check if event is recurring (should not show delete button)
  const isRecurringEvent = event.recurring;

  // Check calendar permissions for external events
  const calendarIds = useMemo(() => (isExternalEvent ? [event.calendarId!] : []), [isExternalEvent, event.calendarId]);

  const { data: calendarPermissions, refetch: refetchCalendarPermissions } = useCronofyCheckCalendarPermissions(
    calendarIds,
    calendarIds.length > 0,
  );

  // Check if calendar has unrestricted permissions
  const hasUnrestrictedAccess = useMemo(() => {
    if (!isExternalEvent || !calendarPermissions) return false;
    return hasUnrestrictedPermissions(calendarPermissions, [event.calendarId!]);
  }, [isExternalEvent, calendarPermissions, event.calendarId]);

  // Hooks for mutations
  const { mutateAsync: manageExternalEvent } = useCronofyManageExternalEvent({
    onSuccess: (_, variables) => {
      const { operation } = variables;
      const queryCache = queryClient.getQueryCache();
      const cronofyEventQueries = queryCache
        .getAll()
        .filter((query) => query.queryKey.includes('cronofy') && query.queryKey.includes('events'));

      const clientQueryKeys = cronofyEventQueries.map((q) => q.queryKey);

      setTimeout(() => {
        refetchQueries(clientQueryKeys);
        toast({
          title: operation === 'delete' ? t('toasts.eventDeleted') : t('toasts.eventRescheduled'),
          status: 'success',
          duration: 3000,
          isClosable: true,
        });
        if (operation === 'delete') {
          setIsDeleting(false);
          onClose();
          onEventDeleted?.();
        } else {
          setIsUpdating(false);
          onCloseRescheduleConfirmDialog();
          onClose();
        }
        // In case of some delay, we need to refetch again the queries to update the event in the calendar
        setTimeout(() => {
          refetchQueries(clientQueryKeys);
        }, 1000);
      }, 3000);
    },
  });
  const { mutateAsync: requestExtendedPermissions } = useCronofyRequestExtendedPermissions();

  // Extended Permissions Manager
  const permissionsManager = useMemo(
    () => new CronofyExtendedPermissionsManager(requestExtendedPermissions),
    [requestExtendedPermissions],
  );

  const eventStatus = (status: string) => {
    const statusMap: { [key: string]: string } = {
      declined: t('statuses.declined'),
      needs_action: t('statuses.needsAction'),
      accepted: t('statuses.accepted'),
      tentative: t('statuses.tentative'),
    };
    return statusMap[status] || t('statuses.unknown');
  };

  const handleDeleteClick = () => {
    if (!hasUnrestrictedAccess) {
      onOpenPermissionsDialog();
    } else {
      onOpenConfirmDialog();
    }
  };

  const handleRequestPermissions = async () => {
    if (!event.calendarId) return;

    setIsRequestingPermissions(true);

    // CRITICAL: Open popup IMMEDIATELY to avoid browser blocking
    // This must be the first action, before any async operations
    const popup = permissionsManager.openPermissionsPopupImmediate();

    if (!popup) {
      return;
    }

    try {
      const result = await permissionsManager.requestExtendedPermissionsWithPopup([event.calendarId], popup, t);

      if (result.success) {
        // Refetch permissions and get fresh data
        const { data: freshCalendarPermissions } = await refetchCalendarPermissions();

        // Calculate permissions directly with fresh data
        const hasUnrestrictedAccessNow = freshCalendarPermissions
          ? hasUnrestrictedPermissions(freshCalendarPermissions, [event.calendarId!])
          : false;

        if (hasUnrestrictedAccessNow) {
          // After permissions are granted, allow the user to delete
          onOpenConfirmDialog();
        } else {
          toast({
            title: t('toasts.permissionsDenied'),
            description: result.error || t('toasts.permissionsNotGrantedFallback'),
            status: 'warning',
            duration: 5000,
            isClosable: true,
          });
        }
      } else {
        toast({
          title: t('toasts.permissionsDenied'),
          description: result.error,
          status: 'warning',
          duration: 5000,
          isClosable: true,
        });
      }
      onClosePermissionsDialog();
    } catch (error) {
      console.error('Error requesting extended permissions:', error);
      // Make sure to close popup on error
      if (!popup.closed) {
        popup.close();
      }
      onClosePermissionsDialog();
      toast({
        title: t('toasts.permissionsError'),
        description: error instanceof Error ? error.message : 'Unknown error',
        status: 'error',
        duration: 5000,
        isClosable: true,
      });
    } finally {
      setIsRequestingPermissions(false);
    }
  };

  const handleConfirmDelete = async () => {
    if (!event.calendarId || !event.id) return;

    setIsDeleting(true);

    try {
      await manageExternalEvent({
        calendarId: event.calendarId,
        eventId: event.id,
        operation: 'delete',
      });
    } catch (error) {
      console.error('Error deleting external event:', error);
      toast({
        title: t('toasts.eventDeleteError'),
        description: error instanceof Error ? error.message : 'Unknown error',
        status: 'error',
        duration: 5000,
        isClosable: true,
      });
    }
  };

  const handleEditClick = () => {
    if (!hasUnrestrictedAccess) {
      onOpenPermissionsDialog();
    } else {
      // Reset validation error when opening edit dialog
      setTimeValidationError(null);
      onOpenEditDialog();
    }
  };

  const validateDateChanges = (): boolean => {
    // Validate duration format and minimum duration
    const durationMinutes = durationStringToMinutes(duration);
    if (durationMinutes < 5) {
      setTimeValidationError('Duration must be at least 5 minutes');
      return false;
    }

    setTimeValidationError(null);
    return true;
  };

  const handleSaveEdit = () => {
    if (!validateDateChanges()) {
      return;
    }

    onCloseEditDialog();
    onOpenRescheduleConfirmDialog();
  };

  const handleStartTimeChange = (date: Date | null) => {
    if (date) {
      setNewStartTime(date);
    }
  };

  const handleDurationChange = (newDuration: string) => {
    setDuration(newDuration);
  };

  const handleConfirmReschedule = async () => {
    if (!event.calendarId || !event.id) return;

    setIsUpdating(true);

    try {
      // Calculate end time based on start time + duration
      const durationMinutes = durationStringToMinutes(duration);
      const calculatedEndTime = addMinutes(newStartTime, durationMinutes);

      await manageExternalEvent({
        calendarId: event.calendarId,
        eventId: event.id,
        operation: 'update',
        eventData: {
          eventId: event.id,
          summary: event.title,
          description: event.description,
          start: newStartTime.toISOString(),
          end: calculatedEndTime.toISOString(),
          tzid: Intl.DateTimeFormat().resolvedOptions().timeZone,
        },
      });
    } catch (error) {
      console.error('Error rescheduling external event:', error);
      toast({
        title: t('toasts.eventRescheduleError'),
        description: error instanceof Error ? error.message : 'Unknown error',
        status: 'error',
        duration: 5000,
        isClosable: true,
      });
      setIsUpdating(false);
    }
  };

  // Determine if delete button should be shown
  const shouldShowDeleteButton = isExternalEvent;

  return (
    <>
      <StyledModal isOpen={isOpen} onClose={onClose} size="lg">
        <ModalHeader>
          <HStack>
            {event?.resource?.icon && (
              <Flex
                padding={rem(5)}
                borderRadius={99}
                backgroundColor={event?.resource?.isExternal ? 'gray.300' : event?.resource?.color}
              >
                <Icon as={event?.resource?.icon} boxSize={rem(14)} />
              </Flex>
            )}

            <Text textStyle="h5SatMedium" noOfLines={eventLength <= '30m' ? 1 : 2}>
              {title}
            </Text>
          </HStack>

          <VStack alignItems="start" marginTop={rem(10)}>
            <HStack>
              <Text textStyle="medium" fontWeight="bold">
                {t('labels.start')}:
              </Text>
              <Text textStyle="medium">
                {format(new Date(event?.start), 'dd-MM-yyyy hh:mm a', { locale: dateLocale })}
              </Text>

              <Text textStyle="medium" fontWeight="bold">
                {t('labels.end')}:
              </Text>
              <Text textStyle="medium">
                {format(new Date(event?.end), 'dd-MM-yyyy hh:mm a', { locale: dateLocale })}
              </Text>
            </HStack>

            <HStack>
              <Text textStyle="medium" fontWeight="bold">
                {t('labels.duration')}:
              </Text>
              <Text textStyle="medium">{eventLength}</Text>
            </HStack>
            <HStack>
              <Text textStyle="medium" fontWeight="bold">
                {t('labels.participationStatus')}:
              </Text>
              <Text textStyle="medium">{eventStatus(event.participationStatus || '')}</Text>
            </HStack>
          </VStack>
        </ModalHeader>

        <ModalCloseButton />

        {event?.description && (
          <ModalBody padding={`${rem(16)} ${rem(24)}`}>
            <Divider marginBottom={rem(10)} />
            <Text textStyle="medium">
              <div dangerouslySetInnerHTML={{ __html: event?.description }} />
            </Text>
          </ModalBody>
        )}

        {shouldShowDeleteButton && (
          <ModalFooter justifyContent="space-between" gap={rem(16)} display="flex">
            <HStack gap={rem(16)}>
              <Tooltip
                color="black.500"
                background="gray.200"
                hasArrow
                isDisabled={!isRecurringEvent}
                label={isRecurringEvent ? <Text>{t('buttons.disabledEventTooltip')}</Text> : t('buttons.editEvent')}
                placement="right"
              >
                <IconButton
                  opacity={isRecurringEvent ? 0.5 : 1}
                  _hover={
                    isRecurringEvent
                      ? {
                          cursor: 'not-allowed',
                        }
                      : undefined
                  }
                  cursor={isRecurringEvent ? 'not-allowed' : 'pointer'}
                  aria-label={t('buttons.editEvent')}
                  icon={<Icon as={IconPencilDraw} boxSize={rem(20)} />}
                  isDisabled={isRecurringEvent}
                  onClick={isRecurringEvent ? undefined : handleEditClick}
                  variant="secondary"
                />
              </Tooltip>
              <Tooltip
                color="black.500"
                background="gray.200"
                hasArrow
                isDisabled={!isRecurringEvent}
                label={isRecurringEvent ? <Text>{t('buttons.disabledEventTooltip')}</Text> : t('buttons.deleteEvent')}
                placement="right"
              >
                <IconButton
                  opacity={isRecurringEvent ? 0.5 : 1}
                  _hover={
                    isRecurringEvent
                      ? {
                          cursor: 'not-allowed',
                        }
                      : undefined
                  }
                  cursor={isRecurringEvent ? 'not-allowed' : 'pointer'}
                  aria-label={t('buttons.deleteEvent')}
                  icon={<Icon as={IconTrash} boxSize={rem(20)} />}
                  isDisabled={isRecurringEvent}
                  onClick={isRecurringEvent ? undefined : handleDeleteClick}
                  variant="secondary"
                />
              </Tooltip>
            </HStack>
          </ModalFooter>
        )}
      </StyledModal>

      {/* Permissions Dialog */}
      <DialogModal
        isOpen={isPermissionsDialogOpen}
        onCancel={onClosePermissionsDialog}
        title={t('permissionsDialog.title')}
        message={t('permissionsDialog.message')}
        confirmText={t('permissionsDialog.grantButton')}
        cancelText={t('permissionsDialog.cancelButton')}
        onConfirm={handleRequestPermissions}
        isButtonConfirmLoading={isRequestingPermissions}
        confirmVariant="primary"
      />

      {/* Delete Confirmation Dialog */}
      <DialogModal
        isOpen={isConfirmDialogOpen}
        onCancel={onCloseConfirmDialog}
        title={t('confirmDialog.title')}
        message={t('confirmDialog.message')}
        confirmText={t('confirmDialog.deleteButton')}
        cancelText={t('confirmDialog.cancelButton')}
        onConfirm={handleConfirmDelete}
        isButtonConfirmLoading={isDeleting}
        confirmVariant="danger"
      />

      {/* Edit Event Dialog */}
      <StyledModal isOpen={isEditDialogOpen} onClose={onCloseEditDialog} size="md">
        <ModalHeader>
          <Text textStyle="h5SatMedium">{t('editDialog.title', { eventTitle: event.title })}</Text>
        </ModalHeader>
        <ModalCloseButton />
        <ModalBody paddingTop={rem(1)} paddingBottom={rem(16)} paddingX={rem(24)}>
          <VStack spacing={rem(16)}>
            <Text textStyle="small" alignSelf="start" marginBottom={rem(8)} opacity={0.8}>
              {t('editDialog.note')}
            </Text>

            {timeValidationError && (
              <Text textStyle="small" marginBottom={rem(8)} color="red.500">
                {timeValidationError}
              </Text>
            )}

            <HStack align="start" gap={rem(16)} width="full">
              <VStack align="stretch" spacing={rem(8)}>
                <RPMDateTimePickerWithPlaceholder
                  scheduledDate={newStartTime}
                  scheduledTime={newStartTime}
                  isDatePickerOpen={isStartDatePickerOpen}
                  updateSelectedDate={handleStartTimeChange}
                  updateScheduledTime={handleStartTimeChange}
                  onDatePickerOpen={onStartDatePickerOpen}
                  onSave={onStartDatePickerClose}
                  onCancel={onStartDatePickerClose}
                  onClickUnplan={() => {}}
                  placeholder="Select date and time"
                  tabIndex={DAY}
                  withTimePicker
                  showDayWeekSelector={false}
                />
              </VStack>
              <VStack align="stretch" flex="0 0 auto" spacing={rem(8)}>
                <InputDuration value={duration} onChange={handleDurationChange} variant="bordered" />
              </VStack>
            </HStack>
          </VStack>
        </ModalBody>
        <ModalFooter gap={rem(16)}>
          <Button onClick={onCloseEditDialog} variant="secondary">
            {t('editDialog.cancel')}
          </Button>
          <Button onClick={handleSaveEdit} variant="primary">
            {t('editDialog.save')}
          </Button>
        </ModalFooter>
      </StyledModal>

      {/* Reschedule Confirmation Dialog */}
      <DialogModal
        isOpen={isRescheduleConfirmDialogOpen}
        onCancel={onCloseRescheduleConfirmDialog}
        title={t('rescheduleDialog.title')}
        message={t('rescheduleDialog.message')}
        confirmText={t('rescheduleDialog.confirmButton')}
        cancelText={t('rescheduleDialog.cancelButton')}
        onConfirm={handleConfirmReschedule}
        isButtonConfirmLoading={isUpdating}
        confirmVariant="primary"
      />
    </>
  );
}

export default EventDetailModal;
